import { MigrationRegistry as IMigrationRegistry, Migration } from './types';

export class MigrationRegistry implements IMigrationRegistry {
  private migrations = new Map<string, Migration>();
  private versionGraph = new Map<string, string[]>(); // version -> list of next versions

  register(migration: Migration): void {
    this.migrations.set(migration.id, migration);
    this.buildVersionGraph();
  }

  get(id: string): Migration | undefined {
    return this.migrations.get(id);
  }

  getByVersion(version: string): Migration[] {
    return Array.from(this.migrations.values()).filter(m => m.version === version);
  }

  getPath(fromVersion: string, toVersion: string): Migration[] {
    const path = this.findMigrationPath(fromVersion, toVersion);
    return path.map(migrationId => this.migrations.get(migrationId)).filter(Boolean) as Migration[];
  }

  list(): Migration[] {
    return Array.from(this.migrations.values()).sort((a, b) => this.compareVersions(a.version, b.version));
  }

  private buildVersionGraph(): void {
    this.versionGraph.clear();
    
    // Build directed graph of version transitions
    for (const migration of this.migrations.values()) {
      // Extract source version from migration requirements or infer from version
      const sourceVersion = this.inferSourceVersion(migration);
      const targetVersion = migration.version;
      
      if (!this.versionGraph.has(sourceVersion)) {
        this.versionGraph.set(sourceVersion, []);
      }
      
      const targets = this.versionGraph.get(sourceVersion)!;
      if (!targets.includes(targetVersion)) {
        targets.push(targetVersion);
      }
    }
  }

  private inferSourceVersion(migration: Migration): string {
    // This is a simplified version - in practice, migrations would explicitly declare their source version
    const version = migration.version;
    const parts = version.split('.').map(Number);
    
    // For now, assume each migration upgrades from the previous minor version
    if (parts[1] > 0) {
      return `${parts[0]}.${parts[1] - 1}.${parts[2]}`;
    } else if (parts[0] > 1) {
      return `${parts[0] - 1}.0.0`;
    }
    
    return '1.0.0';
  }

  private findMigrationPath(fromVersion: string, toVersion: string): string[] {
    // Use Dijkstra's algorithm to find shortest migration path
    const distances = new Map<string, number>();
    const previous = new Map<string, string | null>();
    const unvisited = new Set<string>();
    
    // Initialize all versions
    const allVersions = new Set([fromVersion, toVersion]);
    for (const migration of this.migrations.values()) {
      allVersions.add(migration.version);
      allVersions.add(this.inferSourceVersion(migration));
    }
    
    for (const version of allVersions) {
      distances.set(version, Infinity);
      previous.set(version, null);
      unvisited.add(version);
    }
    
    distances.set(fromVersion, 0);
    
    while (unvisited.size > 0) {
      // Find unvisited vertex with minimum distance
      let current: string | null = null;
      let minDistance = Infinity;
      
      for (const vertex of unvisited) {
        const distance = distances.get(vertex)!;
        if (distance < minDistance) {
          minDistance = distance;
          current = vertex;
        }
      }
      
      if (current === null || current === toVersion) {
        break; // No path found or reached target
      }
      
      unvisited.delete(current);
      
      // Check neighbors
      const neighbors = this.versionGraph.get(current) || [];
      for (const neighbor of neighbors) {
        if (!unvisited.has(neighbor)) continue;
        
        const alt = distances.get(current)! + 1; // Each migration has weight 1
        if (alt < distances.get(neighbor)!) {
          distances.set(neighbor, alt);
          previous.set(neighbor, current);
        }
      }
    }
    
    // Reconstruct path
    const path: string[] = [];
    let current: string | null = toVersion;
    
    while (current !== null && current !== fromVersion) {
      // Find migration that leads to current version
      const migration = Array.from(this.migrations.values()).find(m => m.version === current);
      if (migration) {
        path.unshift(migration.id);
      }
      current = previous.get(current) || null;
    }
    
    return path;
  }

  private compareVersions(a: string, b: string): number {
    const aParts = a.split('.').map(Number);
    const bParts = b.split('.').map(Number);
    
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aPart = aParts[i] || 0;
      const bPart = bParts[i] || 0;
      
      if (aPart > bPart) return 1;
      if (aPart < bPart) return -1;
    }
    
    return 0;
  }

  // Additional utility methods
  getLatestVersion(): string {
    const versions = Array.from(this.migrations.values())
      .map(m => m.version)
      .filter((v, i, arr) => arr.indexOf(v) === i); // unique versions
    
    if (versions.length === 0) return '1.0.0';
    
    return versions.sort((a, b) => this.compareVersions(b, a))[0];
  }

  getVersions(): string[] {
    return Array.from(this.migrations.values())
      .map(m => m.version)
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .sort((a, b) => this.compareVersions(a, b));
  }

  getBreakingMigrations(): Migration[] {
    return Array.from(this.migrations.values()).filter(m => m.breaking);
  }

  getMigrationsByType(type: string): Migration[] {
    return Array.from(this.migrations.values()).filter(m => m.type === type);
  }

  validate(): { valid: boolean; issues: string[] } {
    const issues: string[] = [];
    
    // Check for duplicate migration IDs
    const ids = new Set<string>();
    for (const migration of this.migrations.values()) {
      if (ids.has(migration.id)) {
        issues.push(`Duplicate migration ID: ${migration.id}`);
      }
      ids.add(migration.id);
    }
    
    // Check for orphaned versions (versions with no path from 1.0.0)
    const reachableVersions = new Set<string>();
    const queue = ['1.0.0'];
    reachableVersions.add('1.0.0');
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      const neighbors = this.versionGraph.get(current) || [];
      
      for (const neighbor of neighbors) {
        if (!reachableVersions.has(neighbor)) {
          reachableVersions.add(neighbor);
          queue.push(neighbor);
        }
      }
    }
    
    const allVersions = new Set<string>();
    for (const migration of this.migrations.values()) {
      allVersions.add(migration.version);
      allVersions.add(this.inferSourceVersion(migration));
    }
    
    for (const version of allVersions) {
      if (!reachableVersions.has(version) && version !== '1.0.0') {
        issues.push(`Orphaned version (no migration path): ${version}`);
      }
    }
    
    return {
      valid: issues.length === 0,
      issues
    };
  }

  // Debugging and visualization helpers
  exportGraph(): { [version: string]: string[] } {
    const graph: { [version: string]: string[] } = {};
    
    for (const [version, targets] of this.versionGraph.entries()) {
      graph[version] = targets;
    }
    
    return graph;
  }

  printMigrationTree(): void {
    console.log('Migration Tree:');
    const versions = this.getVersions();
    
    for (const version of versions) {
      const migrations = this.getByVersion(version);
      console.log(`\n${version}:`);
      
      for (const migration of migrations) {
        console.log(`  - ${migration.id}: ${migration.description}`);
        if (migration.breaking) {
          console.log(`    ⚠️  BREAKING CHANGE`);
        }
      }
    }
  }

  clear(): void {
    this.migrations.clear();
    this.versionGraph.clear();
  }

  size(): number {
    return this.migrations.size;
  }

  hasVersion(version: string): boolean {
    return Array.from(this.migrations.values()).some(m => m.version === version);
  }

  getMigrationChain(fromVersion: string, toVersion: string): Array<{
    migration: Migration;
    fromVersion: string;
    toVersion: string;
    breaking: boolean;
  }> {
    const path = this.getPath(fromVersion, toVersion);
    const chain: Array<{
      migration: Migration;
      fromVersion: string;
      toVersion: string;
      breaking: boolean;
    }> = [];
    
    let currentVersion = fromVersion;
    
    for (const migration of path) {
      chain.push({
        migration,
        fromVersion: currentVersion,
        toVersion: migration.version,
        breaking: migration.breaking
      });
      currentVersion = migration.version;
    }
    
    return chain;
  }
}